import { KeyboardEventHandler, RefObject, useRef, useState } from "react";
import { useKeyboardFocusables } from "../../../hooks";

/**
 * Responsible for orchestrating the input and calendar popover states
 * @param inputRef - The ref to the input element
 * @returns calendarOpen, setCalendarOpen, handleCalendarKeyDown, handleInputKeyDown
 */
export const useDateFieldOrchestration = ({
  inputRef,
  calendarDefaultOpen,
  popoverContentRef,
  disableCalendar = false,
}: {
  inputRef: RefObject<HTMLInputElement>;
  calendarDefaultOpen: boolean;
  popoverContentRef: RefObject<HTMLDivElement>;
  disableCalendar?: boolean;
}) => {
  const documentRef = useRef<HTMLElement>(document.body);
  const [calendarOpen, setCalendarOpen] = useState(calendarDefaultOpen);

  const { focusables } = useKeyboardFocusables(documentRef, {
    observeChange: true,
  });

  const { focusables: popoverFocusables } = useKeyboardFocusables(
    popoverContentRef,
    {
      observeChange: true,
    },
  );

  const pageFocusables = focusables?.filter(
    (item) => !popoverFocusables?.includes(item),
  );

  const handleCalendarKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    if (event.key === "Escape") {
      inputRef.current?.focus();
    }
  };

  const focusToCalendar = () => {
    if (popoverContentRef.current) {
      const currentFocusable =
        popoverContentRef.current.querySelectorAll('[tabindex = "0"]')[0];
      if (currentFocusable) {
        (currentFocusable as HTMLButtonElement).focus();
      }
    }
  };

  const handleInputKeyDown: KeyboardEventHandler<HTMLInputElement> = (ev) => {
    if (disableCalendar) {
      return;
    }
    let currentFocusIndex = 0;
    switch (ev.key) {
      case "Escape":
        setCalendarOpen(false);
        break;
      case "Tab":
        if (!calendarOpen || !pageFocusables?.length) {
          break;
        }
        ev.preventDefault();
        currentFocusIndex = pageFocusables.indexOf(inputRef.current!) || 0;
        setCalendarOpen(false);
        if (ev.shiftKey) {
          if (currentFocusIndex === 0) {
            requestAnimationFrame(() =>
              pageFocusables[pageFocusables.length - 1].focus(),
            );
          } else {
            requestAnimationFrame(() =>
              pageFocusables[currentFocusIndex - 1].focus(),
            );
          }
          break;
        }
        if (pageFocusables.length > currentFocusIndex + 1) {
          requestAnimationFrame(() =>
            pageFocusables[currentFocusIndex + 1].focus(),
          );
        } else {
          requestAnimationFrame(() => pageFocusables[0].focus());
        }
        break;
      case "ArrowDown":
        if (!calendarOpen) {
          setCalendarOpen(true);
          setTimeout(focusToCalendar, 200);
        } else {
          focusToCalendar();
        }
    }
  };

  return {
    calendarOpen,
    setCalendarOpen,
    handleCalendarKeyDown,
    handleInputKeyDown,
  };
};
